import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  LOCATION_LOCK_STORAGE_KEY,
  LOCATION_STORAGE_KEY,
  type DeliveryLocation,
} from '../constants/location'
import { parseStoredLocation, serializeLocation } from '../helpers/location'

type DeliveryLocationContextValue = {
  location: DeliveryLocation | null
  /** Convenience alias for catalog / header display. */
  city: string | null
  isModalOpen: boolean
  locationLocked: boolean
  openModal: () => void
  closeModal: () => void
  selectLocation: (location: DeliveryLocation) => void
  lockLocation: () => void
  unlockLocation: () => void
}

const DeliveryLocationContext =
  createContext<DeliveryLocationContextValue | null>(null)

function readStoredLocation(): DeliveryLocation | null {
  if (typeof window === 'undefined') return null
  return parseStoredLocation(window.localStorage.getItem(LOCATION_STORAGE_KEY))
}

function readLocationLock(): boolean {
  if (typeof window === 'undefined') return false
  return window.sessionStorage.getItem(LOCATION_LOCK_STORAGE_KEY) === '1'
}

export function DeliveryLocationProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<DeliveryLocation | null>(() =>
    readStoredLocation(),
  )
  const [isModalOpen, setIsModalOpen] = useState(
    () => readStoredLocation() === null && !readLocationLock(),
  )
  const [locationLocked, setLocationLocked] = useState(readLocationLock)

  useEffect(() => {
    if (!location) {
      window.localStorage.removeItem(LOCATION_STORAGE_KEY)
      return
    }
    window.localStorage.setItem(LOCATION_STORAGE_KEY, serializeLocation(location))
  }, [location])

  useEffect(() => {
    if (!isModalOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isModalOpen])

  const value = useMemo<DeliveryLocationContextValue>(
    () => ({
      location,
      city: location?.city ?? null,
      isModalOpen,
      locationLocked,
      openModal: () => {
        if (locationLocked) return
        setIsModalOpen(true)
      },
      closeModal: () => setIsModalOpen(false),
      selectLocation: (next) => {
        if (locationLocked) return
        setLocation(next)
        setIsModalOpen(false)
      },
      lockLocation: () => {
        window.sessionStorage.setItem(LOCATION_LOCK_STORAGE_KEY, '1')
        setLocationLocked(true)
        setIsModalOpen(false)
      },
      unlockLocation: () => {
        window.sessionStorage.removeItem(LOCATION_LOCK_STORAGE_KEY)
        setLocationLocked(false)
      },
    }),
    [location, isModalOpen, locationLocked],
  )

  return (
    <DeliveryLocationContext.Provider value={value}>
      {children}
    </DeliveryLocationContext.Provider>
  )
}

export function useDeliveryLocation(): DeliveryLocationContextValue {
  const value = useContext(DeliveryLocationContext)
  if (!value) {
    throw new Error('useDeliveryLocation must be used within DeliveryLocationProvider')
  }
  return value
}
